// Markdown rendering tuned for Notes — built on `marked` with three
// extensions plus a slugged-heading renderer:
//
//   • [[wikilink]] / [[wikilink|display]]
//   • #tag (must follow whitespace or line-start so we don't catch
//     hashes inside URLs, hex colors, or "this#that" id refs)
//   • headings get id="..." so the outline panel can scrollIntoView
//
// `render(content, ctx)` is the only export. The reader passes a
// `resolveWikilink(target)` callback that returns the matching note
// path or null (built from the flat note list — cheap, no extra RPC).
// Unresolved wikilinks render with `data-missing="true"` so we can
// dim them in CSS and offer a "Create" affordance later.

import { Marked, type Tokens } from "marked";

export interface MarkdownContext {
  /** Returns the resolved note path for a wikilink target, or null. */
  resolveWikilink: (target: string) => string | null;
}

interface WikilinkToken {
  type: "wikilink";
  raw: string;
  target: string;
  display: string;
}
interface TagToken {
  type: "taglink";
  raw: string;
  tag: string;
}

// One module-scoped marked instance, configured once. Extensions are
// idempotent registrations; calling `use` again with the same name
// replaces them, so reloads in dev don't accumulate handlers.
const instance = new Marked({
  gfm: true,
  breaks: false,
});

instance.use({
  extensions: [
    {
      name: "wikilink",
      level: "inline",
      start(src: string) { return src.indexOf("[["); },
      tokenizer(src: string) {
        const m = /^\[\[([^\]\n|]+?)(?:\|([^\]\n]+?))?\]\]/.exec(src);
        if (!m) return undefined;
        return {
          type: "wikilink",
          raw: m[0],
          target: m[1].trim(),
          display: (m[2] || m[1]).trim(),
        };
      },
      renderer(token: Tokens.Generic) {
        const t = token as unknown as WikilinkToken;
        // The rendered element carries the raw target as data; the
        // reader resolves it at click time so a note being added /
        // renamed after render doesn't strand stale anchors.
        return `<a class="wikilink" data-wikilink="${escapeAttr(t.target)}">${escapeHtml(t.display)}</a>`;
      },
    },
    {
      name: "taglink",
      level: "inline",
      // #tag only when preceded by a non-word char (or start of line);
      // start() returns the FIRST candidate position so marked can
      // efficiently skip ahead. We return the offset of the `#` so the
      // tokenizer sees a leading "#".
      start(src: string) {
        const m = /(?:^|[^\w])#[A-Za-z][\w-]*/.exec(src);
        if (!m) return undefined;
        return m[0].startsWith("#") ? m.index : m.index + 1;
      },
      tokenizer(src: string) {
        const m = /^#([A-Za-z][\w-]*)/.exec(src);
        if (!m) return undefined;
        return { type: "taglink", raw: m[0], tag: m[1] };
      },
      renderer(token: Tokens.Generic) {
        const t = token as unknown as TagToken;
        return `<a class="taglink" data-tag="${escapeAttr(t.tag)}">#${escapeHtml(t.tag)}</a>`;
      },
    },
  ],
});

// Slugged-heading renderer. The default renderer skips the id
// attribute; we add it so the outline panel can document.getElementById
// and scrollIntoView without us inserting custom anchor markup.
instance.use({
  renderer: {
    heading(this: any, token: Tokens.Heading): string {
      const text = this.parser.parseInline(token.tokens || []);
      const slug = slugify(token.text);
      return `<h${token.depth} id="${slug}">${text}</h${token.depth}>\n`;
    },
  },
});

export function render(content: string, ctx: MarkdownContext): string {
  // Pre-resolve all wikilink targets so we can decorate missing ones
  // with `data-missing`. The renderer above doesn't have access to the
  // context, so this post-pass on the HTML adds the marker.
  const html = instance.parse(content || "", { async: false }) as string;
  return html.replace(/<a class="wikilink" data-wikilink="([^"]+)"/g, (_match, target: string) => {
    const decoded = unescapeAttr(target);
    const resolved = ctx.resolveWikilink(decoded);
    if (resolved) {
      return `<a class="wikilink" data-wikilink="${escapeAttr(resolved)}"`;
    }
    return `<a class="wikilink" data-wikilink="${target}" data-missing="true"`;
  });
}

// Outline extraction — runs against the raw markdown source so we can
// build the panel without touching the rendered HTML. Slugs match the
// renderer's slugify so the click-to-scroll handler can use getElementById.
export interface OutlineHeading {
  depth: number;
  text: string;
  slug: string;
}

export function extractOutline(content: string): OutlineHeading[] {
  if (!content) return [];
  const out: OutlineHeading[] = [];
  // Strip fenced code blocks before scanning so a `## a comment` inside
  // a code fence doesn't show up in the outline.
  const stripped = content.replace(/```[\s\S]*?```/g, "");
  const re = /^(#{1,6})\s+(.+?)\s*#*\s*$/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(stripped)) !== null) {
    out.push({
      depth: m[1].length,
      text: m[2].trim(),
      slug: slugify(m[2]),
    });
  }
  return out;
}

// GitHub-flavored slugger: lowercase, hyphenate, drop most punctuation.
// Repeat slugs would normally get -1/-2 suffixes; we don't track that
// here because the outline scrollIntoView lands on the FIRST occurrence
// either way and duplicates are rare in real notes.
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/<[^>]+>/g, "")          // strip inline html
    .replace(/[`*_~]/g, "")            // strip md emphasis chars
    .replace(/[^\w\s-]/g, "")          // strip punctuation
    .trim()
    .replace(/\s+/g, "-");
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function unescapeAttr(s: string): string {
  return s.replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
